import { buildGenericTooltipRows } from './tooltipHelpers';

import {
    type ReportWidgetItem,
    type ReportWidgetItemMeta,
    type ReportWidgetTooltipRowConfig,
    type ReportWidgetTooltipRowsBuilder,
    getChartItemId,
} from '../types';

const PRINT_CHART_WIDTH_FALLBACK = 720;
const PRINT_GROUP_GAP = 24;
const PRINT_LEGEND_WIDTH_ESTIMATE = 280;
const PRINT_PIE_OUTER_RADIUS = 86;
const PRINT_DONUT_INNER_RADIUS = 48;
const PRINT_GROUP_RIGHT_NUDGE = 30;

export interface PieChartLayout {
    useRightSideLegend: boolean;
    pieCx: number | string;
    pieInnerRadius: number | string;
    pieOuterRadius: number | string;
    legendLeft?: number;
}

export const resolvePieChartLayout = (params: {
    isPrinting: boolean;
    showLegend: boolean;
    width?: number;
    variant: 'pie' | 'donut';
}): PieChartLayout => {
    // For printing, we want to place the legend to the right of the chart.
    // This impacts the layout of the chart in a variety of ways, so we need to compute the layout accordingly.
    const { isPrinting, showLegend, width, variant } = params;
    const useRightSideLegend = isPrinting && showLegend;

    if (!useRightSideLegend) {
        return {
            useRightSideLegend,
            pieCx: '50%',
            pieInnerRadius: variant === 'donut' ? '40%' : 0,
            pieOuterRadius: '80%',
        };
    }

    const printChartWidth =
        typeof width === 'number' && width > 0
            ? width
            : PRINT_CHART_WIDTH_FALLBACK;

    const printPieDiameter = PRINT_PIE_OUTER_RADIUS * 2;
    const printGroupWidth =
        printPieDiameter + PRINT_GROUP_GAP + PRINT_LEGEND_WIDTH_ESTIMATE;
    const printGroupLeft = Math.max(
        0,
        (printChartWidth - printGroupWidth) / 2 + PRINT_GROUP_RIGHT_NUDGE,
    );

    return {
        useRightSideLegend,
        pieCx: printGroupLeft + PRINT_PIE_OUTER_RADIUS,
        pieInnerRadius: variant === 'donut' ? PRINT_DONUT_INNER_RADIUS : 0,
        pieOuterRadius: PRINT_PIE_OUTER_RADIUS,
        legendLeft: printGroupLeft + printPieDiameter + PRINT_GROUP_GAP,
    };
};

function getAggregatedCount(meta?: ReportWidgetItemMeta): number | null {
    const count = meta?.aggregatedCount;
    return typeof count === 'number' && count > 0 ? count : null;
}

export function getAggregatedOtherLabel(params?: {
    modelLabel?: string;
    meta?: ReportWidgetItemMeta;
}): string {
    const modelLabel = params?.modelLabel;
    const aggregatedCount = getAggregatedCount(params?.meta);

    if (aggregatedCount != null && modelLabel) {
        return `${aggregatedCount} ${modelLabel}`;
    }

    if (modelLabel) {
        return `Other ${modelLabel}`;
    }

    return 'Other';
}

export function resolvePieTooltipRowsBuilder(params: {
    items: ReportWidgetItem[];
    modelLabel?: string;
    tooltipRowsBuilder?: ReportWidgetTooltipRowsBuilder;
}): ReportWidgetTooltipRowsBuilder {
    const { items, modelLabel, tooltipRowsBuilder } = params;

    return (args) => {
        const entry = args.payload?.[0];
        const pointPayload = entry?.payload as
            | { id?: string; name?: string }
            | undefined;
        const itemId = pointPayload?.id;
        const matchedItem = itemId
            ? items.find((item) => getChartItemId(item) === itemId)
            : undefined;

        if (matchedItem?.meta?.isAggregatedOther) {
            const aggregatedOtherLabel = getAggregatedOtherLabel({
                modelLabel,
                meta: matchedItem.meta,
            });
            const aggregatedHeaderRows: ReportWidgetTooltipRowConfig[] = [
                {
                    id: 'header',
                    variant: 'header',
                    label: aggregatedOtherLabel,
                    boldLabel: true,
                    mutedLabel: true,
                },
            ];

            if (tooltipRowsBuilder) {
                const aggregatedRows = tooltipRowsBuilder({
                    ...args,
                    aggregation: {
                        kind: 'other',
                        label: aggregatedOtherLabel,
                        item: matchedItem,
                        headerRows: aggregatedHeaderRows,
                    },
                });

                if (aggregatedRows && aggregatedRows.length > 0) {
                    return aggregatedRows;
                }
            }

            return aggregatedHeaderRows;
        }

        if (tooltipRowsBuilder) {
            return tooltipRowsBuilder(args) ?? [];
        }

        return buildGenericTooltipRows(args);
    };
}
